import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Save, Clock, AlertTriangle } from "lucide-react";

const DAYS = [
  { label: "Sun", value: 0 },
  { label: "Mon", value: 1 },
  { label: "Tue", value: 2 },
  { label: "Wed", value: 3 },
  { label: "Thu", value: 4 },
  { label: "Fri", value: 5 },
  { label: "Sat", value: 6 },
];

const TIMEZONES = [
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Anchorage",
  "Pacific/Honolulu",
  "UTC",
  "Europe/London",
  "Europe/Berlin",
  "Europe/Paris",
  "Asia/Tokyo",
  "Asia/Singapore",
  "Australia/Sydney",
];

function buildTimeOptions(): { label: string; value: string }[] {
  const opts: { label: string; value: string }[] = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 30) {
      const hh = String(h).padStart(2, "0");
      const mm = String(m).padStart(2, "0");
      const value = `${hh}:${mm}`;
      const amPm = h < 12 ? "AM" : "PM";
      const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
      opts.push({ label: `${h12}:${mm} ${amPm}`, value });
    }
  }
  return opts;
}
const TIME_OPTIONS = buildTimeOptions();

interface OfficeHoursConfig {
  id?: string;
  enabled: boolean;
  daysOfWeek: number[];
  startLocal: string;
  endLocal: string;
  timezone: string;
  updatedAt?: string;
}

interface OfficeHoursResponse {
  config: OfficeHoursConfig | null;
  currentlyOpen: boolean;
  nextOpenAt: string | null;
}

interface DeferralRow {
  id: string;
  scheduler: string;
  itemKind: string;
  itemId: string;
  nextOpenAt: string;
  deferredAt: string;
}

const DEFAULT_CONFIG: OfficeHoursConfig = {
  enabled: false,
  daysOfWeek: [1, 2, 3, 4, 5],
  startLocal: "09:00",
  endLocal: "17:00",
  timezone: "America/Chicago",
};

export function PlatformOfficeHoursPanel() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery<OfficeHoursResponse>({
    queryKey: ["/api/platform/admin/office-hours"],
  });

  const { data: deferrals = [] } = useQuery<DeferralRow[]>({
    queryKey: ["/api/platform/admin/office-hours/deferrals"],
  });

  const [form, setForm] = useState<OfficeHoursConfig>(DEFAULT_CONFIG);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (data?.config) {
      setForm({ ...DEFAULT_CONFIG, ...data.config });
      setDirty(false);
    }
  }, [data]);

  const setField = <K extends keyof OfficeHoursConfig>(k: K, v: OfficeHoursConfig[K]) => {
    setForm((f) => ({ ...f, [k]: v }));
    setDirty(true);
  };

  const toggleDay = (d: number) => {
    const next = form.daysOfWeek.includes(d)
      ? form.daysOfWeek.filter((x) => x !== d)
      : [...form.daysOfWeek, d].sort((a, b) => a - b);
    setField("daysOfWeek", next);
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("PUT", "/api/platform/admin/office-hours", form);
      if (!res.ok) throw new Error((await res.json())?.message ?? "Save failed");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/platform/admin/office-hours"] });
      setDirty(false);
      toast({ title: "Saved", description: "Platform office hours updated." });
    },
    onError: (err: any) => {
      toast({ title: "Error", description: err?.message, variant: "destructive" });
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground py-8 justify-center">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading…
      </div>
    );
  }

  const isOpen = data?.currentlyOpen ?? true;
  const nextOpenAt = data?.nextOpenAt ? new Date(data.nextOpenAt) : null;

  return (
    <div className="space-y-6">
      <Card data-testid="card-platform-office-hours">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
              <Clock className="w-4 h-4 text-primary" />
            </div>
            <div className="flex-1">
              <CardTitle className="text-base">Platform Send Window</CardTitle>
              <CardDescription>
                Gates all automated outbound — drip campaign steps and mass email blasts. Manual
                one-off sends from the Email Hub always bypass this window.
              </CardDescription>
            </div>
            {form.enabled && (
              <Badge
                variant={isOpen ? "default" : "secondary"}
                className="shrink-0"
                data-testid="badge-platform-oh-status"
              >
                {isOpen ? "Open" : "Closed"}
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Enable toggle */}
          <div className="flex items-center justify-between rounded-lg border border-border p-3 max-w-lg">
            <div>
              <div className="text-sm font-medium">Enable platform office hours</div>
              <div className="text-xs text-muted-foreground">
                When off, automated sends are never gated by this window.
              </div>
            </div>
            <Switch
              checked={form.enabled}
              onCheckedChange={(v) => setField("enabled", v)}
              data-testid="switch-platform-oh-enabled"
            />
          </div>

          {form.enabled && (
            <>
              {/* Closed warning */}
              {!isOpen && nextOpenAt && (
                <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-700 px-3 py-2 text-sm text-amber-800 dark:text-amber-300">
                  <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                  <span>
                    Send window is currently{" "}
                    <strong>closed</strong>. Automated sends will resume{" "}
                    <strong>{nextOpenAt.toLocaleString()}</strong>.
                  </span>
                </div>
              )}

              {/* Days of week */}
              <div className="space-y-2">
                <Label className="text-sm">Allowed send days</Label>
                <div className="flex gap-2 flex-wrap" data-testid="day-toggles-platform-oh">
                  {DAYS.map((d) => {
                    const selected = form.daysOfWeek.includes(d.value);
                    return (
                      <button
                        key={d.value}
                        type="button"
                        onClick={() => toggleDay(d.value)}
                        data-testid={`button-day-${d.value}`}
                        className={[
                          "w-10 h-10 rounded-full text-xs font-medium border transition-colors",
                          selected
                            ? "bg-primary text-primary-foreground border-primary"
                            : "bg-transparent text-muted-foreground border-border hover:border-primary/50",
                        ].join(" ")}
                      >
                        {d.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Time range */}
              <div className="flex items-center gap-3 flex-wrap">
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Start (local time)</Label>
                  <Select
                    value={form.startLocal}
                    onValueChange={(v) => setField("startLocal", v)}
                  >
                    <SelectTrigger className="w-36" data-testid="select-platform-oh-start">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TIME_OPTIONS.map((t) => (
                        <SelectItem key={t.value} value={t.value}>
                          {t.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <span className="text-sm text-muted-foreground pt-5">to</span>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">End (local time)</Label>
                  <Select
                    value={form.endLocal}
                    onValueChange={(v) => setField("endLocal", v)}
                  >
                    <SelectTrigger className="w-36" data-testid="select-platform-oh-end">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TIME_OPTIONS.map((t) => (
                        <SelectItem key={t.value} value={t.value}>
                          {t.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Timezone */}
              <div className="space-y-1.5 max-w-xs">
                <Label className="text-xs text-muted-foreground">Timezone</Label>
                <Select
                  value={form.timezone}
                  onValueChange={(v) => setField("timezone", v)}
                >
                  <SelectTrigger data-testid="select-platform-oh-tz">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TIMEZONES.map((tz) => (
                      <SelectItem key={tz} value={tz}>
                        {tz}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}

          <Button
            size="sm"
            disabled={saveMutation.isPending || !dirty}
            onClick={() => saveMutation.mutate()}
            data-testid="button-save-platform-oh"
          >
            {saveMutation.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            Save
          </Button>
        </CardContent>
      </Card>

      {/* Deferral log */}
      {deferrals.length > 0 && (
        <Card data-testid="card-platform-oh-deferrals">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-500" />
              Recent Deferrals
              <Badge variant="secondary">{deferrals.length}</Badge>
            </CardTitle>
            <CardDescription className="text-xs">
              Automated sends blocked by the platform window. They will be automatically
              retried once the window reopens — no manual action required.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {deferrals.slice(0, 20).map((row) => (
                <div
                  key={row.id}
                  className="flex items-start justify-between gap-3 rounded-md border px-3 py-2 text-xs"
                  data-testid={`row-deferral-${row.id}`}
                >
                  <div className="space-y-0.5">
                    <div className="font-medium capitalize">
                      {row.itemKind.replace(/-/g, " ")}
                    </div>
                    <div className="text-muted-foreground font-mono text-[10px]">
                      {row.scheduler} · {row.itemId.slice(0, 18)}…
                    </div>
                    <div className="text-muted-foreground">
                      Deferred {new Date(row.deferredAt).toLocaleString()}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-muted-foreground">Reopen</div>
                    <div className="font-medium">{new Date(row.nextOpenAt).toLocaleString()}</div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
